// src/components/Pages/Setting.js
import React, { useState, useEffect, useRef, useCallback } from "react";
import toast from "react-hot-toast";
import { useAuth } from "../../contexts/AuthContext";
import { useNotifications } from "../../contexts/NotificationsContext";
import {
  Settings,
  Database,
  Bell,
  Globe,
  Users,
  Lock,
  Download,
  Upload,
  CheckCircle,
  Save,
  AlertTriangle,
  Server,
  HardDrive,
} from "lucide-react";

/* ---------------- Tailwind UI helpers ---------------- */

function Card({ children, className = "" }) {
  return (
    <div
      className={`rounded-2xl border border-gray-200 bg-white shadow-sm
        dark:border-gray-800 dark:bg-gray-900 ${className}`}
    >
      {children}
    </div>
  );
}

function CardHeader({ children, className = "" }) {
  return (
    <div
      className={`px-6 py-4 border-b border-gray-100 dark:border-gray-800 ${className}`}
    >
      {children}
    </div>
  );
}

function CardTitle({ children, className = "" }) {
  return (
    <h3
      className={`text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2 ${className}`}
    >
      {children}
    </h3>
  );
}

function CardContent({ children, className = "" }) {
  return <div className={`px-6 py-5 ${className}`}>{children}</div>;
}

function Input({ className = "", ...props }) {
  return (
    <input
      {...props}
      className={`w-full h-11 rounded-xl border border-gray-300 bg-white px-4 text-sm text-gray-900 shadow-sm outline-none
        focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500
        dark:border-gray-700 dark:bg-gray-950 dark:text-gray-100 ${className}`}
    />
  );
}

function Button({
  children,
  variant = "default",
  className = "",
  type = "button",
  ...props
}) {
  const base =
    "inline-flex items-center justify-center h-11 px-5 rounded-xl text-sm font-semibold shadow-sm transition focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed";
  const styles =
    variant === "outline"
      ? "bg-white text-gray-900 border border-gray-200 hover:bg-gray-50 dark:bg-gray-900 dark:text-gray-100 dark:border-gray-800 dark:hover:bg-gray-800"
      : "bg-indigo-600 text-white hover:bg-indigo-700";

  return (
    <button type={type} {...props} className={`${base} ${styles} ${className}`}>
      {children}
    </button>
  );
}

function Badge({ children, className = "" }) {
  return (
    <span
      className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-medium ${className}`}
    >
      {children}
    </span>
  );
}

function Toggle({ checked, onChange }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-7 w-14 items-center rounded-full transition ${
        checked ? "bg-indigo-600" : "bg-gray-300 dark:bg-gray-700"
      }`}
      aria-pressed={checked}
      aria-label="Toggle"
    >
      <span
        className={`inline-block h-6 w-6 transform rounded-full bg-white transition ${
          checked ? "translate-x-7" : "translate-x-1"
        }`}
      />
    </button>
  );
}

/* ---------------- Datetime helpers ---------------- */

function toDateTimeLocal(value) {
  if (!value) return "";

  const str = String(value).trim();

  // Already correct for input[type=datetime-local]
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(str)) {
    return str;
  }

  // MySQL DATETIME: YYYY-MM-DD HH:mm:ss -> YYYY-MM-DDTHH:mm
  const m = str.match(
    /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::\d{2})?$/
  );

  if (!m) return "";

  const [, y, mo, d, h, mi] = m;
  return `${y}-${mo}-${d}T${h}:${mi}`;
}

function fromDateTimeLocal(value) {
  return value?.trim() || null;
}

/* ---------------- Uptime helpers ---------------- */

function parseUptimeToSeconds(value) {
  if (typeof value === "number" && Number.isFinite(value)) {
    return Math.max(0, Math.floor(value));
  }

  const str = String(value || "").trim();
  if (!str) return 0;

  const fullMatch = str.match(
    /(?:(\d+)\s*d)?\s*(?:(\d+)\s*h)?\s*(?:(\d+)\s*m)?\s*(?:(\d+)\s*s)?/i
  );

  if (fullMatch) {
    const days = Number(fullMatch[1] || 0);
    const hours = Number(fullMatch[2] || 0);
    const mins = Number(fullMatch[3] || 0);
    const secs = Number(fullMatch[4] || 0);
    return days * 86400 + hours * 3600 + mins * 60 + secs;
  }

  return 0;
}

function formatUptime(seconds) {
  const total = Math.max(0, Number(seconds) || 0);
  const days = Math.floor(total / 86400);
  const hours = Math.floor((total % 86400) / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const secs = total % 60;
  return `${days}d ${hours}h ${minutes}m ${secs}s`;
}

/* -------------------------------- Sections -------------------------------- */

function GeneralSettingsSection({
  draftSettings,
  setDraftField,
  onReset,
  saving,
  loading,
}) {
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>
            <Settings className="h-5 w-5" />
            Organization Details
          </CardTitle>
        </CardHeader>

        <CardContent className="space-y-5">
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Organization Name
              </label>
              <Input
                value={draftSettings.organizationName ?? ""}
                onChange={(e) =>
                  setDraftField("organizationName", e.target.value)
                }
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Email Address
              </label>
              <Input
                type="email"
                value={draftSettings.email ?? ""}
                onChange={(e) => setDraftField("email", e.target.value)}
              />
            </div>

            <div className="md:col-span-2">
              <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Address
              </label>
              <Input
                value={draftSettings.address ?? ""}
                onChange={(e) => setDraftField("address", e.target.value)}
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Phone Number
              </label>
              <Input
                value={draftSettings.phone ?? ""}
                onChange={(e) => setDraftField("phone", e.target.value)}
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Website
              </label>
              <Input
                value={draftSettings.website ?? ""}
                onChange={(e) => setDraftField("website", e.target.value)}
              />
            </div>
          </div>

          <div className="flex flex-wrap gap-2 pt-2">
            <Button
              variant="outline"
              onClick={onReset}
              disabled={saving || loading}
            >
              Reset Changes
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function SecuritySettingsSection({ draftSettings, setDraftField }) {
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>
            <Lock className="h-5 w-5" />
            Password Policy
          </CardTitle>
        </CardHeader>

        <CardContent className="space-y-5">
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Minimum Password Length
              </label>
              <Input
                type="number"
                value={draftSettings.passwordMinLength ?? 8}
                onChange={(e) =>
                  setDraftField("passwordMinLength", Number(e.target.value))
                }
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Password Expiry (days)
              </label>
              <Input
                type="number"
                value={draftSettings.passwordExpiryDays ?? 90}
                onChange={(e) =>
                  setDraftField("passwordExpiryDays", Number(e.target.value))
                }
              />
            </div>
          </div>

          <div className="space-y-3 text-sm text-gray-700 dark:text-gray-300">
            {[
              { key: "requireUppercase", label: "Require uppercase letters" },
              { key: "requireLowercase", label: "Require lowercase letters" },
              { key: "requireNumbers", label: "Require numbers" },
              { key: "requireSpecial", label: "Require special characters" },
            ].map((item) => (
              <label key={item.key} className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={!!draftSettings[item.key]}
                  onChange={(e) => setDraftField(item.key, e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300"
                />
                {item.label}
              </label>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>
            <Users className="h-5 w-5" />
            Access Control
          </CardTitle>
        </CardHeader>

        <CardContent className="space-y-5">
          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Failed Login Attempts Limit
            </label>
            <Input
              type="number"
              value={draftSettings.failedLoginLimit ?? 5}
              onChange={(e) =>
                setDraftField("failedLoginLimit", Number(e.target.value))
              }
            />
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Account Lockout Duration (minutes)
            </label>
            <Input
              type="number"
              value={draftSettings.lockoutMinutes ?? 15}
              onChange={(e) =>
                setDraftField("lockoutMinutes", Number(e.target.value))
              }
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function NotificationSettingsSection({
  notifState,
  setNotifState,
  onSaveNotificationsOnly,
  saving,
  loading,
}) {
  const labelMap = {
    system_updates: "System Update",
    health_records: "Health Alerts",
    development_milestones: "Development Milestones",
    donations: "Donation Alerts",
    user_activity: "User Activity",
    data_backup: "Data Backup",
    alerts: "Alerts",
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex items-center justify-between gap-3">
          <CardTitle>
            <Bell className="h-5 w-5" />
            Notification Preferences
          </CardTitle>

          <Button
            variant="outline"
            onClick={onSaveNotificationsOnly}
            disabled={saving || loading}
            className="h-10 px-4"
          >
            Save Notifications
          </Button>
        </CardHeader>

        <CardContent>
          <div className="space-y-4">
            {notifState.map((n) => (
              <div
                key={n.id}
                className="flex items-center justify-between gap-4 rounded-xl border border-gray-200 p-4
                    dark:border-gray-800 dark:bg-gray-950/40"
              >
                <div className="min-w-0">
                  <h4 className="font-semibold text-gray-900 dark:text-gray-100">
                    {labelMap[n.type] || n.type}
                  </h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {n.description}
                  </p>
                </div>

                <Toggle
                  checked={n.enabled}
                  onChange={(next) =>
                    setNotifState((prev) =>
                      prev.map((x) =>
                        x.id === n.id ? { ...x, enabled: next } : x
                      )
                    )
                  }
                />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function BackupSettingsSection({
  systemStats,
  createBackup,
  restoreBackup,
  backupLoading,
  backupList,
  backupListLoading,
  downloadBackup,
  statusLoading,
  maintenance,
  setMaintenance,
  saveMaintenance,
  maintenanceSaving,
}) {
  const fileRef = useRef(null);

  const latest = backupList?.[0];
  const latestDate = latest?.createdAt
    ? new Date(latest.createdAt).toLocaleString()
    : "No backups yet";
  const latestSize = latest?.size
    ? `${(latest.size / (1024 * 1024)).toFixed(2)} MB`
    : "";

  const usedGB = systemStats?.storage?.usedGB;
  const totalGB = systemStats?.storage?.totalGB;

  const healthOk = systemStats?.healthStatus === "healthy";
  const dbOk = systemStats?.checks?.database?.status === "ok";
  const storageOk = systemStats?.checks?.storage?.status === "ok";
  const apiOk = systemStats?.checks?.api?.status === "ok";

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>
            <Database className="h-5 w-5" />
            System Status
          </CardTitle>
        </CardHeader>

        <CardContent>
          <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
            <div
              className={`rounded-2xl p-5 text-center border ${
                healthOk
                  ? "bg-green-50 border-green-100 dark:bg-green-950/30 dark:border-green-900"
                  : "bg-red-50 border-red-100 dark:bg-red-950/30 dark:border-red-900"
              }`}
            >
              {healthOk ? (
                <CheckCircle className="mx-auto mb-2 h-8 w-8 text-green-600" />
              ) : (
                <AlertTriangle className="mx-auto mb-2 h-8 w-8 text-red-600" />
              )}

              <p className="font-semibold text-gray-900 dark:text-gray-100">
                System Health
              </p>
              <p
                className={`text-sm mt-1 ${
                  healthOk ? "text-green-700" : "text-red-700"
                }`}
              >
                {statusLoading
                  ? "Checking..."
                  : healthOk
                  ? "Healthy"
                  : "Problem detected"}
              </p>
            </div>

            <div className="rounded-2xl bg-blue-50 p-5 text-center border border-blue-100 dark:bg-blue-950/30 dark:border-blue-900">
              <Database className="mx-auto mb-2 h-8 w-8 text-blue-600" />
              <p className="font-semibold text-gray-900 dark:text-gray-100">
                Storage Used
              </p>
              <p className="text-blue-700 text-sm mt-1">
                {typeof usedGB === "number" && typeof totalGB === "number"
                  ? `${usedGB}GB / ${totalGB}GB`
                  : "—"}
              </p>
            </div>

            <div className="rounded-2xl bg-purple-50 p-5 text-center border border-purple-100 dark:bg-purple-950/30 dark:border-purple-900">
              <Globe className="mx-auto mb-2 h-8 w-8 text-purple-600" />
              <p className="font-semibold text-gray-900 dark:text-gray-100">
                Uptime
              </p>
              <p className="text-purple-700 text-sm mt-1">
                {systemStats?.uptime || "—"}
              </p>
            </div>
          </div>

          <div className="mt-5 grid grid-cols-1 gap-3 md:grid-cols-3">
            <div className="rounded-xl border border-gray-200 dark:border-gray-800 p-4">
              <div className="flex items-center gap-2">
                <Server
                  className={`h-4 w-4 ${apiOk ? "text-green-600" : "text-red-600"}`}
                />
                <span className="font-medium text-gray-900 dark:text-gray-100">
                  API Server
                </span>
              </div>
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                {systemStats?.checks?.api?.message || "No data"}
              </p>
            </div>

            <div className="rounded-xl border border-gray-200 dark:border-gray-800 p-4">
              <div className="flex items-center gap-2">
                <Database
                  className={`h-4 w-4 ${dbOk ? "text-green-600" : "text-red-600"}`}
                />
                <span className="font-medium text-gray-900 dark:text-gray-100">
                  Database
                </span>
              </div>
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                {systemStats?.checks?.database?.message || "No data"}
              </p>
            </div>

            <div className="rounded-xl border border-gray-200 dark:border-gray-800 p-4">
              <div className="flex items-center gap-2">
                <HardDrive
                  className={`h-4 w-4 ${
                    storageOk ? "text-green-600" : "text-red-600"
                  }`}
                />
                <span className="font-medium text-gray-900 dark:text-gray-100">
                  Storage
                </span>
              </div>
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                {systemStats?.checks?.storage?.message || "No data"}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex items-center justify-between gap-3">
          <CardTitle>
            <Settings className="h-5 w-5" />
            System Maintenance
          </CardTitle>

          <Button
            variant="outline"
            onClick={saveMaintenance}
            disabled={maintenanceSaving || statusLoading}
            className="h-10 px-4"
          >
            <Save className="mr-2 h-4 w-4" />
            {maintenanceSaving ? "Saving..." : "Save"}
          </Button>
        </CardHeader>

        <CardContent className="space-y-4">
          <div className="flex items-center justify-between gap-4 rounded-xl border border-gray-200 p-4 dark:border-gray-800 dark:bg-gray-950/40">
            <div className="min-w-0">
              <h4 className="font-semibold text-gray-900 dark:text-gray-100">
                Maintenance Mode
              </h4>
            </div>

            <Toggle
              checked={!!maintenance?.enabled}
              onChange={(next) =>
                setMaintenance((p) => ({ ...(p || {}), enabled: next }))
              }
            />
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="md:col-span-2">
              <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Maintenance Message
              </label>
              <Input
                value={maintenance?.message ?? ""}
                onChange={(e) =>
                  setMaintenance((p) => ({
                    ...(p || {}),
                    message: e.target.value,
                  }))
                }
                placeholder="We are currently doing maintenance. Please try again later."
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Start Time (optional)
              </label>
              <Input
                type="datetime-local"
                value={maintenance?.startsAt ?? ""}
                onChange={(e) =>
                  setMaintenance((p) => ({
                    ...(p || {}),
                    startsAt: e.target.value,
                  }))
                }
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                End Time (optional)
              </label>
              <Input
                type="datetime-local"
                value={maintenance?.endsAt ?? ""}
                onChange={(e) =>
                  setMaintenance((p) => ({
                    ...(p || {}),
                    endsAt: e.target.value,
                  }))
                }
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>
            <Download className="h-5 w-5" />
            Backup Management
          </CardTitle>
        </CardHeader>

        <CardContent className="space-y-5">
          <div className="flex items-center justify-between gap-4 rounded-2xl bg-green-50 p-5 border border-green-100 dark:bg-green-950/30 dark:border-green-900">
            <div className="min-w-0">
              <h4 className="font-semibold text-gray-900 dark:text-gray-100">
                Last Backup
              </h4>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                {backupListLoading ? "Loading..." : latestDate}
                {!backupListLoading && latestSize ? ` — ${latestSize}` : ""}
              </p>
            </div>
            <Badge className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100">
              {latest ? "Available" : "None"}
            </Badge>
          </div>

          <input
            ref={fileRef}
            type="file"
            accept=".sql"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) restoreBackup(f);
              e.target.value = "";
            }}
          />

          <div className="flex flex-wrap gap-2">
            <Button onClick={createBackup} disabled={backupLoading}>
              <Download className="mr-2 h-4 w-4" />
              {backupLoading ? "Working..." : "Create Backup"}
            </Button>

            <Button
              variant="outline"
              onClick={() => fileRef.current?.click()}
              disabled={backupLoading}
            >
              <Upload className="mr-2 h-4 w-4" />
              {backupLoading ? "Working..." : "Restore Backup"}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>
            <Database className="h-5 w-5" />
            Backup History
          </CardTitle>
        </CardHeader>

        <CardContent>
          {backupListLoading ? (
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Loading backups...
            </p>
          ) : backupList.length === 0 ? (
            <p className="text-sm text-gray-600 dark:text-gray-400">
              No backups found.
            </p>
          ) : (
            <div className="space-y-3">
              {backupList.slice(0, 10).map((b) => (
                <div
                  key={b.filename}
                  className="flex items-center justify-between gap-3 rounded-xl border border-gray-200 p-4 dark:border-gray-800 dark:bg-gray-950/40"
                >
                  <div className="min-w-0">
                    <p className="font-semibold text-gray-900 dark:text-gray-100 truncate">
                      {b.filename}
                    </p>
                    <p className="text-xs text-gray-600 dark:text-gray-400">
                      {b.createdAt
                        ? new Date(b.createdAt).toLocaleString()
                        : ""}
                      {b.size
                        ? ` • ${(b.size / (1024 * 1024)).toFixed(2)} MB`
                        : ""}
                    </p>
                  </div>

                  <Button
                    variant="outline"
                    className="h-10 px-4"
                    onClick={() => downloadBackup(b.filename)}
                  >
                    <Download className="mr-2 h-4 w-4" />
                    Download
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

/* -------------------------------- Main Component -------------------------------- */

export default function Setting() {
  const { user, authFetch } = useAuth();
  const notif = useNotifications();
  const refreshEnabledTypes = notif?.refreshEnabledTypes;

  const [activeSection, setActiveSection] = useState("general");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);

  const [backupLoading, setBackupLoading] = useState(false);
  const [backupList, setBackupList] = useState([]);
  const [backupListLoading, setBackupListLoading] = useState(false);

  const [statusLoading, setStatusLoading] = useState(false);
  const [uptimeSeconds, setUptimeSeconds] = useState(0);

  const [systemStats, setSystemStats] = useState({
    uptime: "—",
    storage: null,
    dbSizeMB: null,
    backupsCount: 0,
    lastBackup: null,
    backupStatus: "—",
    healthStatus: "unknown",
    checks: {
      api: null,
      database: null,
      storage: null,
    },
  });

  const [settings, setSettings] = useState(null);
  const [draftSettings, setDraftSettings] = useState({});
  const [isDirty, setIsDirty] = useState(false);

  const [maintenance, setMaintenance] = useState({
    enabled: false,
    message: "",
    startsAt: "",
    endsAt: "",
  });
  const [maintenanceSaving, setMaintenanceSaving] = useState(false);

  const [notifState, setNotifState] = useState([
    {
      id: 1,
      type: "system_updates",
      enabled: true,
      description: "System maintenance and updates",
    },
    {
      id: 2,
      type: "health_records",
      enabled: true,
      description: "Health records notifications",
    },
    {
      id: 3,
      type: "development_milestones",
      enabled: true,
      description: "Milestone progress notifications",
    },
    {
      id: 4,
      type: "donations",
      enabled: false,
      description: "New donation notifications",
    },
    {
      id: 5,
      type: "user_activity",
      enabled: true,
      description: "User login and activity alerts",
    },
    {
      id: 6,
      type: "data_backup",
      enabled: true,
      description: "Backup completion notifications",
    },
  ]);

  useEffect(() => {
    if (!user) return;

    let cancelled = false;

    (async () => {
      try {
        setLoading(true);

        const res = await authFetch("/settings", { method: "GET" });
        if (!res || cancelled) return;
        if (!res.ok) return;

        const data = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (!data?.success) return;

        if (data.settings) {
          setSettings(data.settings);
          setMaintenance({
            enabled: !!data.settings.maintenanceEnabled,
            message: data.settings.maintenanceMessage || "",
            startsAt: toDateTimeLocal(data.settings.maintenanceStartsAt),
            endsAt: toDateTimeLocal(data.settings.maintenanceEndsAt),
          });
        }

        if (Array.isArray(data.notifState)) setNotifState(data.notifState);
      } catch (err) {
        if (!cancelled) console.error("Load settings error:", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user, authFetch]);

  useEffect(() => {
    if (isDirty || !settings) return;
    setDraftSettings(settings);
  }, [settings, isDirty]);

  const setDraftField = (key, value) => {
    setIsDirty(true);
    setDraftSettings((prev) => ({ ...prev, [key]: value }));
  };

  const buildNextSettings = useCallback(() => {
    return {
      ...draftSettings,
      maintenanceEnabled: !!maintenance?.enabled,
      maintenanceMessage: maintenance?.message || "",
      maintenanceStartsAt: fromDateTimeLocal(maintenance?.startsAt),
      maintenanceEndsAt: fromDateTimeLocal(maintenance?.endsAt),
    };
  }, [draftSettings, maintenance]);

  const validateMaintenanceTime = useCallback(() => {
    if (maintenance?.startsAt && maintenance?.endsAt) {
      const start = new Date(maintenance.startsAt);
      const end = new Date(maintenance.endsAt);

      if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
        toast.error("Invalid maintenance date/time.");
        return false;
      }

      if (start > end) {
        toast.error("End time must be later than start time.");
        return false;
      }
    }
    return true;
  }, [maintenance]);

  const saveMaintenance = useCallback(async () => {
    if (!user) return toast.error("Not logged in.");
    if (!validateMaintenanceTime()) return;

    try {
      setMaintenanceSaving(true);
      toast.loading("Saving maintenance settings...", {
        id: "save-maintenance",
      });

      const nextSettings = buildNextSettings();

      const res = await authFetch("/settings", {
        method: "PUT",
        body: JSON.stringify({ settings: nextSettings, notifState }),
      });

      if (!res) return;

      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        throw new Error(data.message || "Failed to save maintenance");
      }

      setDraftSettings(nextSettings);
      setSettings(nextSettings);
      setIsDirty(false);

      if (typeof refreshEnabledTypes === "function") {
        await refreshEnabledTypes();
      }

      toast.success("Maintenance settings saved.", {
        id: "save-maintenance",
      });
    } catch (e) {
      toast.error(e?.message || "Failed to save maintenance", {
        id: "save-maintenance",
      });
    } finally {
      setMaintenanceSaving(false);
    }
  }, [
    user,
    authFetch,
    buildNextSettings,
    notifState,
    refreshEnabledTypes,
    validateMaintenanceTime,
  ]);

  const handleSaveAll = useCallback(async () => {
    if (!user) return toast.error("Not logged in.");
    if (!validateMaintenanceTime()) return;

    try {
      setSaving(true);
      toast.loading("Saving settings...", { id: "save-settings" });

      const nextSettings = buildNextSettings();

      const res = await authFetch("/settings", {
        method: "PUT",
        body: JSON.stringify({ settings: nextSettings, notifState }),
      });

      if (!res) return;

      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        throw new Error(data.message || "Failed to save");
      }

      setSettings(nextSettings);
      setDraftSettings(nextSettings);
      setIsDirty(false);

      if (typeof refreshEnabledTypes === "function") {
        await refreshEnabledTypes();
      }

      toast.success("Settings saved successfully.", { id: "save-settings" });
    } catch (err) {
      console.error("Save settings error:", err);
      toast.error(err?.message || "Failed to save settings", {
        id: "save-settings",
      });
    } finally {
      setSaving(false);
    }
  }, [
    user,
    authFetch,
    buildNextSettings,
    notifState,
    refreshEnabledTypes,
    validateMaintenanceTime,
  ]);

  const saveNotificationsOnly = useCallback(async () => {
    if (!user) return toast.error("Not logged in.");
    if (!validateMaintenanceTime()) return;

    try {
      setSaving(true);
      toast.loading("Saving notification settings...", {
        id: "save-notifications",
      });

      const nextSettings = buildNextSettings();

      const res = await authFetch("/settings", {
        method: "PUT",
        body: JSON.stringify({ settings: nextSettings, notifState }),
      });

      if (!res) return;

      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) throw new Error(data.message || "Failed");

      setDraftSettings(nextSettings);
      setSettings(nextSettings);

      if (typeof refreshEnabledTypes === "function") {
        await refreshEnabledTypes();
      }

      toast.success("Notification settings saved.", {
        id: "save-notifications",
      });
    } catch (e) {
      toast.error(e?.message || "Failed to save notification settings", {
        id: "save-notifications",
      });
    } finally {
      setSaving(false);
    }
  }, [
    user,
    authFetch,
    buildNextSettings,
    notifState,
    refreshEnabledTypes,
    validateMaintenanceTime,
  ]);

  const fetchHealthStatus = useCallback(async () => {
    if (!user) return;

    try {
      setStatusLoading(true);

      const res = await authFetch("/system/health", { method: "GET" });
      if (!res) return;

      const data = await res.json().catch(() => ({}));

      const nextUptimeSeconds = parseUptimeToSeconds(
        data?.uptimeSeconds ?? data?.uptime
      );

      setUptimeSeconds(nextUptimeSeconds);

      setSystemStats((prev) => ({
        ...prev,
        uptime: formatUptime(nextUptimeSeconds),
        healthStatus: data?.status || "unknown",
        checks: data?.checks || {
          api: null,
          database: null,
          storage: null,
        },
      }));
    } catch (e) {
      console.error("fetchHealthStatus error:", e);
      setSystemStats((prev) => ({
        ...prev,
        healthStatus: "unhealthy",
        checks: {
          api: { status: "error", message: "Health check request failed" },
          database: { status: "error", message: "Unable to verify database" },
          storage: { status: "error", message: "Unable to verify storage" },
        },
      }));
    } finally {
      setStatusLoading(false);
    }
  }, [user, authFetch]);

  const fetchBackupStatus = useCallback(async () => {
    if (!user) return;

    try {
      const res = await authFetch("/backup/status", { method: "GET" });
      if (!res) return;
      if (!res.ok) return;

      const data = await res.json().catch(() => ({}));
      if (!data.success) throw new Error(data.message || "Failed to load status");

      const st = data.status || {};
      setSystemStats((prev) => ({
        ...prev,
        storage: st.storage || null,
        dbSizeMB: st.dbSizeMB ?? null,
        backupsCount: st.backupsCount ?? 0,
        lastBackup: st.lastBackup || null,
        backupStatus: st.backupStatus || "OK",
      }));
    } catch (e) {
      console.error("fetchBackupStatus error:", e);
    }
  }, [user, authFetch]);

  const fetchBackups = useCallback(async () => {
    if (!user) return;

    try {
      setBackupListLoading(true);

      const res = await authFetch("/backup/list", { method: "GET" });
      if (!res) return;
      if (!res.ok) return;

      const data = await res.json().catch(() => ({}));
      if (!data.success) throw new Error(data.message || "Failed to load backups");

      setBackupList(Array.isArray(data.backups) ? data.backups : []);
    } catch (e) {
      console.error(e);
    } finally {
      setBackupListLoading(false);
    }
  }, [user, authFetch]);

  const downloadBackup = useCallback(
    async (filename) => {
      if (!user) return toast.error("Not logged in.");
      toast.loading("Downloading backup...", { id: "download-backup" });

      try {
        const res = await authFetch(
          `/backup/download/${encodeURIComponent(filename)}`,
          { method: "GET" }
        );
        if (!res) return;

        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.message || "Download failed");
        }

        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);

        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        a.click();

        window.URL.revokeObjectURL(url);
        toast.success("Backup downloaded.", { id: "download-backup" });
      } catch (e) {
        toast.error(e.message || "Download failed", {
          id: "download-backup",
        });
      }
    },
    [user, authFetch]
  );

  useEffect(() => {
    if (!user) return;
    if (activeSection === "backup") {
      fetchHealthStatus();
      fetchBackupStatus();
      fetchBackups();
    }
  }, [activeSection, user, fetchHealthStatus, fetchBackupStatus, fetchBackups]);

  useEffect(() => {
    if (!user) {
      setUptimeSeconds(0);
      return;
    }

    const timer = setInterval(() => {
      setUptimeSeconds((prev) => prev + 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [user]);

  useEffect(() => {
    if (!user) return;

    setSystemStats((prev) => ({
      ...prev,
      uptime: formatUptime(uptimeSeconds),
    }));
  }, [uptimeSeconds, user]);

  const createBackup = useCallback(async () => {
    if (!user) return toast.error("Not logged in.");

    try {
      setBackupLoading(true);
      toast.loading("Creating backup...", { id: "create-backup" });

      const res = await authFetch("/backup/create", { method: "POST" });
      if (!res) return;

      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        throw new Error(data.error || data.message || "Backup failed");
      }

      await fetchBackups();
      await fetchBackupStatus();
      await fetchHealthStatus();
      toast.success("Backup created.", { id: "create-backup" });
    } catch (e) {
      toast.error(e?.message || "Backup failed", { id: "create-backup" });
    } finally {
      setBackupLoading(false);
    }
  }, [user, authFetch, fetchBackups, fetchBackupStatus, fetchHealthStatus]);

  const restoreBackup = useCallback(
    async (file) => {
      if (!user) return toast.error("Not logged in.");
      if (!file) return;

      const ok = window.confirm("This will overwrite the database. Continue?");
      if (!ok) return;

      try {
        setBackupLoading(true);
        toast.loading("Restoring backup...", { id: "restore-backup" });

        const form = new FormData();
        form.append("backup", file);

        const res = await authFetch("/backup/restore", {
          method: "POST",
          body: form,
        });

        if (!res) return;

        const data = await res.json().catch(() => ({}));
        if (!res.ok || !data.success) {
          throw new Error(data.error || data.message || "Restore failed");
        }

        await fetchBackups();
        await fetchBackupStatus();
        await fetchHealthStatus();
        toast.success("Backup restored.", { id: "restore-backup" });
      } catch (e) {
        toast.error(e?.message || "Restore failed", { id: "restore-backup" });
      } finally {
        setBackupLoading(false);
      }
    },
    [user, authFetch, fetchBackups, fetchBackupStatus, fetchHealthStatus]
  );

  const resetDraft = () => {
    if (!settings) return;

    setDraftSettings(settings);
    setMaintenance({
      enabled: !!settings.maintenanceEnabled,
      message: settings.maintenanceMessage || "",
      startsAt: toDateTimeLocal(settings.maintenanceStartsAt),
      endsAt: toDateTimeLocal(settings.maintenanceEndsAt),
    });
    setIsDirty(false);
    toast("Changes reset");
  };

  if (!user) return null;

  return (
    <div className="min-h-screen w-full bg-gray-50 dark:bg-gray-950 transition-colors duration-300">
      <div className="w-full min-w-0 p-4 sm:p-6 space-y-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0">
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-gray-900 dark:text-gray-100">
              System Settings
            </h1>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
              Configure system preferences and security settings
            </p>
          </div>

          <div className="flex items-center gap-4 w-full sm:w-auto justify-between sm:justify-end">
            <Button
              onClick={handleSaveAll}
              className="w-full sm:w-auto"
              disabled={saving || loading}
            >
              <Save className="mr-2 h-4 w-4" />
              {saving ? "Saving..." : "Save All Changes"}
            </Button>
          </div>
        </div>

        <Card>
          <CardContent className="p-4">
            <div className="flex gap-2 overflow-x-auto">
              <Button
                variant={activeSection === "general" ? "default" : "outline"}
                className="h-11 px-5 rounded-xl whitespace-nowrap"
                onClick={() => setActiveSection("general")}
              >
                General
              </Button>

              <Button
                variant={activeSection === "security" ? "default" : "outline"}
                className="h-11 px-5 rounded-xl whitespace-nowrap"
                onClick={() => setActiveSection("security")}
              >
                Security
              </Button>

              <Button
                variant={
                  activeSection === "notifications" ? "default" : "outline"
                }
                className="h-11 px-5 rounded-xl whitespace-nowrap"
                onClick={() => setActiveSection("notifications")}
              >
                Notifications
              </Button>

              <Button
                variant={activeSection === "backup" ? "default" : "outline"}
                className="h-11 px-5 rounded-xl whitespace-nowrap"
                onClick={() => setActiveSection("backup")}
              >
                Backup & Maintenance
              </Button>
            </div>
          </CardContent>
        </Card>

        {activeSection === "general" && (
          <GeneralSettingsSection
            draftSettings={draftSettings}
            setDraftField={setDraftField}
            onReset={resetDraft}
            saving={saving}
            loading={loading}
          />
        )}

        {activeSection === "security" && (
          <SecuritySettingsSection
            draftSettings={draftSettings}
            setDraftField={setDraftField}
          />
        )}

        {activeSection === "notifications" && (
          <NotificationSettingsSection
            notifState={notifState}
            setNotifState={setNotifState}
            onSaveNotificationsOnly={saveNotificationsOnly}
            saving={saving}
            loading={loading}
          />
        )}

        {activeSection === "backup" && (
          <BackupSettingsSection
            systemStats={systemStats}
            createBackup={createBackup}
            restoreBackup={restoreBackup}
            backupLoading={backupLoading}
            backupList={backupList}
            backupListLoading={backupListLoading}
            downloadBackup={downloadBackup}
            statusLoading={statusLoading}
            maintenance={maintenance}
            setMaintenance={setMaintenance}
            saveMaintenance={saveMaintenance}
            maintenanceSaving={maintenanceSaving}
          />
        )}
      </div>
    </div>
  );
}